import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageTitle from "../components/PageTitle";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  confirmOrder,
  createOrderFromCart,
  getOrderDetails,
} from "../utils/orderApi";
import { createPaymobPayment } from "../utils/paymobApi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { trackBeginCheckout } from "../utils/analytics";

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // From URL: createdOrderId and optional orderName sent from cart
  const initialOrderId = searchParams.get("createdOrderId");
  const initialOrderName = searchParams.get("orderName") || "";

  const [orderId] = useState(initialOrderId || "");
  const [orderName, setOrderName] = useState(initialOrderName);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState("");

  const [addressLine, setAddressLine] = useState(user?.address_line || "");
  const [addressCity, setAddressCity] = useState(user?.address_city || "");
  const [phone, setPhone] = useState(user?.phone || user?.mobile || "");
  const [paymentMethod, setPaymentMethod] = useState("pay_in_store"); // "pay_in_store" | "card"

  const canCreate = useMemo(() => !!token && items.length > 0, [token, items]);
  const canConfirm = useMemo(
    () => !!token && !!orderId && !!phone && !!addressLine && !!addressCity,
    [token, orderId, phone, addressLine, addressCity]
  );

  useEffect(() => {
    if (!token) {
      navigate("/account-login");
      return;
    }
    // Require an order id to be present (created from cart)
    if (!orderId) navigate("/cart");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Track begin checkout event when user reaches checkout page
  useEffect(() => {
    if (items.length > 0 && subtotal > 0) {
      trackBeginCheckout(items, subtotal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only track once when component mounts

  // Fetch order details to get order name (fallback if not provided in URL)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token || !orderId) return;
      if (orderName) return; // no need to refetch if we already have it
      try {
        const details = await getOrderDetails(orderId, token);
        const ord = Array.isArray(details) ? details[0] : details;
        const name = ord?.order_name || ord?.name || ord?.order || "";
        if (!cancelled) setOrderName(name);
      } catch (_) {}
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [orderId, token, orderName]);

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setError("");
    setIsConfirming(true);
    try {
      if (paymentMethod === "pay_in_store") {
        await confirmOrder(
          orderId,
          token,
          phone,
          addressLine,
          addressCity,
          "pay_in_store"
        );
        clear();
        navigate(`/account/orders`);
        return;
      }

      // Card flow: confirm on Odoo first, then create Paymob payment intention
      await confirmOrder(
        orderId,
        token,
        phone,
        addressLine,
        addressCity,
        "card"
      );

      // Create Paymob payment intention and redirect to unified checkout
      const userName = user?.name || user?.user_name || "";
      const splittedUserName = userName.split(" ");
      const firstName = splittedUserName[0] || "";
      const lastName = splittedUserName.length > 1 ? splittedUserName[1] : "";

      const paymobUrl = await createPaymobPayment(
        orderId,
        subtotal,
        firstName,
        lastName,
        phone,
        user?.email || ""
      );

      // Redirect to Paymob unified checkout
      window.location.href = paymobUrl;
    } catch (e) {
      setError(e?.message || (language === "ar" ? "فشل تأكيد الطلب" : "Failed to confirm order"));
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div>
      <Header />
      <PageTitle title={language === "ar" ? "الدفع" : "Checkout"} />

      <section className="flat-spacing-11">
        <div className="container">
          <div className="row">
            <div className="col-md-7">
              <div className="tf-cart-countdown mb-4">
                <div className="title-left">
                  <svg
                    className="d-inline-block"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="24"
                    viewBox="0 0 16 24"
                    fill="rgb(219 18 21)"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.0899 24C11.3119 22.1928 11.4245 20.2409 10.4277 18.1443C10.1505 19.2691 9.64344 19.9518 8.90645 20.1924C9.59084 18.2379 9.01896 16.1263 7.19079 13.8576C7.15133 16.2007 6.58824 17.9076 5.50148 18.9782C4.00436 20.4517 4.02197 22.1146 5.55428 23.9669C-0.806588 20.5819 -1.70399 16.0418 2.86196 10.347C3.14516 11.7228 3.83141 12.5674 4.92082 12.8809C3.73335 7.84186 4.98274 3.54821 8.66895 0C8.6916 7.87426 11.1062 8.57414 14.1592 12.089C17.4554 16.3071 15.5184 21.1748 10.0899 24Z"
                    />
                  </svg>
                  <p>{language === "ar" ? "راجع عنوانك وطريقة الدفع لإكمال الطلب." : "Review your address and payment to complete order."}</p>
                </div>
              </div>

              <div className="tf-page-cart-wrap">
                <div className="tf-page-cart-item">
                  <div className="mb-3">
                    <h4 className="mb-2">{language === "ar" ? "الطلب" : "Order"}</h4>
                    <div>{language === "ar" ? "رقم الطلب:" : "Order No:"} {orderName || orderId || "—"}</div>
                  </div>

                  <div className="mb-4">
                    <h4 className="mb-2">{language === "ar" ? "عنوان الشحن" : "Shipping Address"}</h4>
                    <div className="row g-2">
                      <div className="col-12">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={language === "ar" ? "الهاتف" : "Phone"}
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>
                      <div className="col-12">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={language === "ar" ? "سطر العنوان" : "Address Line"}
                          value={addressLine}
                          onChange={(e) => setAddressLine(e.target.value)}
                        />
                      </div>
                      <div className="col-12">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={language === "ar" ? "المدينة" : "City"}
                          value={addressCity}
                          onChange={(e) => setAddressCity(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="mb-2">{language === "ar" ? "طريقة الدفع" : "Payment Method"}</h4>
                    <div className="d-flex gap-3">
                      <label className="d-flex align-items-center gap-2">
                        <input
                          type="radio"
                          name="payment_method"
                          checked={paymentMethod === "pay_in_store"}
                          onChange={() => setPaymentMethod("pay_in_store")}
                        />
                        <span>{language === "ar" ? "الدفع في المتجر" : "Pay in store"}</span>
                      </label>
                      <label className="d-flex align-items-center gap-2">
                        <input
                          type="radio"
                          name="payment_method"
                          checked={paymentMethod === "card"}
                          onChange={() => setPaymentMethod("card")}
                        />
                        <span>{language === "ar" ? "بطاقة ائتمان / خصم" : "Credit / Debit Card"}</span>
                      </label>
                    </div>
                  </div>

                  {error ? (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  ) : null}

                  <div className="d-flex gap-3">
                    <button
                      type="button"
                      className="tf-btn btn-fill radius-3"
                      disabled={!canConfirm || isConfirming}
                      onClick={handleConfirm}
                    >
                      {isConfirming ? (language === "ar" ? "جاري المعالجة..." : "Processing...") : (language === "ar" ? "تأكيد الطلب" : "Confirm Order")}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-5">
              <div className="tf-page-cart-checkout">
                <div className="tf-cart-totals-discounts">
                  <h3>{language === "ar" ? "المجموع الفرعي" : "Subtotal"}</h3>
                  <span className="total-value">
                    {(subtotal || 0).toFixed(2)} EGP
                  </span>
                </div>
                <p className="tf-cart-tax">
                  {language === "ar" ? "الضرائب والشحن تُحسب عند التأكيد" : "Taxes and shipping calculated at confirmation"}
                </p>
                <div className="tf-page-cart_imgtrust">
                  <p className="text-center fw-6">{language === "ar" ? "ضمان الدفع الآمن" : "Guarantee Safe Checkout"}</p>
                  <div className="cart-list-social">
                    <div className="payment-item">Visa</div>
                    <div className="payment-item">Mastercard</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
